import React, { forwardRef } from 'react';
import classnames from 'classnames';

// Components
import WpIcon from '../icon/WpIcon';
import WpText from '../text/WpText';

// Styles
import './WpFeedbackButtons.scss';

export type Size = 'sm' | 'md' | 'lg';
const DEFAULT_SIZE = 'md';

export interface Props {
  /**
   * Callback function called when a feedback response is selected
   * @param response - true for positive feedback, false for negative feedback
   */
  onResponse: (response: boolean) => void;
  /**
   * The currently selected response
   * true = positive selected, false = negative selected, null/undefined = no selection
   */
  selectedResponse?: boolean | null;
  /**
   * Label for the positive feedback button
   * @default 'Yes'
   */
  positiveLabel?: string;
  /**
   * Label for the negative feedback button
   * @default 'No'
   */
  negativeLabel?: string;
  /**
   * Size of the feedback buttons
   * Options: 'sm' | 'md' | 'lg'
   * @default 'md'
   */
  size?: Size;
  /**
   * Whether the feedback buttons are disabled
   * @default false
   */
  disabled?: boolean;
  /**
   * Whether to show only icons without text labels
   * @default false
   */
  iconOnly?: boolean;
  /**
   * QA test identifier
   */
  qaTest?: string;
}

const baseClassName = 'WpFeedbackButtons';

const WpFeedbackButtons = forwardRef<HTMLDivElement, Props>(function WpFeedbackButtons(
  {
    onResponse,
    selectedResponse,
    positiveLabel = 'Yes',
    negativeLabel = 'No',
    size = DEFAULT_SIZE,
    disabled = false,
    iconOnly = false,
    qaTest,
  },
  ref
) {
  const handlePositiveClick = () => {
    if (!disabled) {
      onResponse(true);
    }
  };

  const handleNegativeClick = () => {
    if (!disabled) {
      onResponse(false);
    }
  };

  return (
    <div
      className={classnames(
        baseClassName,
        `${baseClassName}--${size}`,
        disabled && `${baseClassName}--disabled`,
        iconOnly && `${baseClassName}--icon-only`
      )}
      ref={ref}
      data-test={qaTest}
    >
      {/* Using a native button here instead of WpButton so we can support red/green background
          colors in this context */}
      <button
        type="button"
        className={classnames(
          `${baseClassName}__button`,
          `${baseClassName}__button--positive`,
          selectedResponse === true && `${baseClassName}__button--selected`,
          disabled && `${baseClassName}__button--disabled`
        )}
        onClick={handlePositiveClick}
        disabled={disabled}
        aria-pressed={selectedResponse === true}
        aria-label={iconOnly ? positiveLabel : undefined}
        title={iconOnly ? positiveLabel : undefined}
        data-test={qaTest ? `${qaTest}-positive` : undefined}
      >
        <WpIcon name="thumbs-up" size={size === 'lg' ? 'md' : 'sm'} />
        {!iconOnly && (
          <WpText tag="span" variant="bodySm" noMargin>
            {positiveLabel}
          </WpText>
        )}
      </button>
      <button
        type="button"
        className={classnames(
          `${baseClassName}__button`,
          `${baseClassName}__button--negative`,
          selectedResponse === false && `${baseClassName}__button--selected`,
          disabled && `${baseClassName}__button--disabled`
        )}
        onClick={handleNegativeClick}
        disabled={disabled}
        aria-pressed={selectedResponse === false}
        aria-label={iconOnly ? negativeLabel : undefined}
        title={iconOnly ? negativeLabel : undefined}
        data-test={qaTest ? `${qaTest}-negative` : undefined}
      >
        <WpIcon name="thumbs-down" size={size === 'lg' ? 'md' : 'sm'} />
        {!iconOnly && (
          <WpText tag="span" variant="bodySm" noMargin>
            {negativeLabel}
          </WpText>
        )}
      </button>
    </div>
  );
});

export default WpFeedbackButtons;
